<template>
  <div
    class="relative"
    :class="{
      'pt-3': isCheckInProduct && freedomFriendsSelfInfo.data.value?.active,
    }"
  >
    <FriendsLabel
      v-if="isCheckInProduct && freedomFriendsSelfInfo.data.value?.active"
      class="absolute -top-1 left-4 z-10"
      is-free
    />
    <div class="bg-background-neutral-secondary rounded-xl p-4">
      <template v-if="isShapagatProduct">
        <div class="space-y-4">
          <div class="flex items-start justify-between space-x-3">
            <div class="flex items-start space-x-3">
              <img
                :src="serviceIcon"
                width="40"
                height="40"
                class="shrink-0"
                alt=""
              />
              <div class="space-y-0.5">
                <p class="text-text-neutral-primary font-semibold leading-5">
                  {{ product.title }}
                </p>
                <div
                  v-if="product.extra_data.carbon_footprint"
                  class="text-text-neutral-quaternary space-y-0.5 text-sm"
                  v-html="
                    product.short_description.replace(
                      '{{carbon_footprint}}',
                      parseInt(props.product.extra_data.carbon_footprint || 0),
                    )
                  "
                ></div>
              </div>
            </div>
            <button
              type="button"
              class="shrink-0 rounded-full active:shadow-md"
              @click="bottomSheet.show(bottomSheetName)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="none"
              >
                <path
                  class="fill-situational-muted"
                  d="M10 1.667a8.333 8.333 0 1 0 0 16.666 8.333 8.333 0 0 0 0-16.666Z"
                />
                <path
                  class="fill-background-neutral-secondary"
                  d="M10 5.833A.833.833 0 1 0 10 7.5a.833.833 0 0 0 0-1.667ZM10.833 10a.833.833 0 0 0-1.667 0v3.333a.833.833 0 1 0 1.667 0V10Z"
                />
              </svg>
            </button>
          </div>
          <div class="flex items-center space-x-2">
            <button
              v-for="(value, idx) in product.schema.amount.allowed.filter(
                amount => amount > 0,
              )"
              :key="idx"
              class="shapagat-option"
              :show-sprout-icon="
                value !== 0 && isSelected && productState.data?.amount === value
              "
              @click="updateProductData(value)"
            >
              <span
                class="relative w-full rounded-xl border px-4 py-2 text-sm font-medium duration-300"
                :class="[
                  productState.data?.amount === value
                    ? 'border-background-positive-primary text-static-white bg-background-positive-primary'
                    : 'border-situational-lightgrey bg-background-neutral-secondary text-text-neutral-tertiary',
                ]"
                >{{ prettyPrice(String(value)) }} ₸</span
              >
            </button>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="space-y-3">
          <div
            class="border-situational-lightgrey flex items-center justify-between space-x-3 border-b pb-4"
          >
            <div class="flex items-center space-x-3">
              <img
                :src="serviceIcon"
                width="40"
                height="40"
                class="shrink-0"
                alt=""
              />
              <p class="text-text-neutral-primary font-semibold">
                {{ product.title }}
              </p>
            </div>
            <button
              type="button"
              class="shrink-0 rounded-full active:shadow-md"
              @click="bottomSheet.show(bottomSheetName)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="none"
              >
                <path
                  class="fill-situational-muted"
                  d="M10 1.667a8.333 8.333 0 1 0 0 16.666 8.333 8.333 0 0 0 0-16.666Z"
                />
                <path
                  class="fill-background-neutral-secondary"
                  d="M10 5.833A.833.833 0 1 0 10 7.5a.833.833 0 0 0 0-1.667ZM10.833 10a.833.833 0 0 0-1.667 0v3.333a.833.833 0 1 0 1.667 0V10Z"
                />
              </svg>
            </button>
          </div>
          <div class="flex items-center justify-between">
            <p
              v-price="product.price.amount"
              class="text-text-neutral-primary font-medium"
            ></p>
            <FreeSwitch
              :checked="Boolean(productState?.id)"
              @onChange="updateProductState"
            />
          </div>
        </div>
      </template>
    </div>
    <ProductDetailsBottomSheet
      :allow-to-add-product="product.service !== 'shapagat'"
      :product="product"
      :name="bottomSheetName"
      :state="productsState[product.service]"
      @onChange="updateProductState"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { isNil } from 'lodash-es';
  import { FreeSwitch } from 'freedom-shared';
  import { v4 as uuid } from 'uuid';
  import { controllerModel } from 'shared';
  import type {
    MarkedProduct,
    PackageInfo,
    ProductsState,
  } from '@aviata/products-fns';
  import type { Airflow } from '@aviata/platform-types';
  import { prettyPrice } from '@aviata/text-fns';

  import { useFreedomFriendsSelfInfo, FriendsLabel } from '@/features/friends';

  import ProductDetailsBottomSheet from './ProductDetailsBottomSheet.vue';

  const props = defineProps<{
    product: MarkedProduct;
    productsState: ProductsState;
    packageInfo: PackageInfo;
  }>();

  const emit = defineEmits<{
    onProductAdd: [productState: Airflow.Products.Service];
    onProductRemove: [productName: Airflow.Products.Service];
    onProductDataUpdate: [productName: Airflow.Products.Service, value: number];
  }>();

  const isShapagatProduct = computed(
    () => props.product.service === 'shapagat',
  );

  const isCheckInProduct = computed(() => props.product.service === 'checkin');

  const freedomFriendsSelfInfo = useFreedomFriendsSelfInfo();

  const bottomSheet = controllerModel.useBottomSheet();
  const bottomSheetName = `product-details-bottom-sheet-${uuid()}`;
  const serviceIcon = computed(() => {
    return new URL(
      `/src/assets/images/${getImgName(props.product.service)}.svg`,
      import.meta.url,
    ).href;
  });
  const productState = computed(
    () => props.productsState[props.product.service],
  );

  const isSelected = computed(() => !isNil(productState.value?.id));

  function updateProductState() {
    if (isSelected.value) {
      emit('onProductRemove', props.product.service);
      return;
    }

    emit('onProductAdd', props.product.service);
  }

  function updateProductData(value: number) {
    if (
      (isSelected.value && productState.value?.data?.amount === value) ||
      value === 0
    ) {
      emit('onProductRemove', props.product.service);
      emit('onProductDataUpdate', props.product.service, 0);
      return;
    }

    if (!isSelected.value) {
      emit('onProductAdd', props.product.service);
    }
    emit('onProductDataUpdate', props.product.service, value);
  }

  function getImgName(service: string) {
    switch (service) {
      case 'sms-schedule':
      case 'sms-ticket':
      case 'sms-reminder':
      case 'sms-plan':
      case 'sms-info':
        return 'sms-product-icon';
      case 'superflex':
        return 'superflex-icon';
      case 'checkin':
        return 'checkin-icon';
      case 'charity-bolashak':
        return 'charity-bolashak-icon';
      case 'shapagat':
        return 'shapagat-tree-icon';
      default:
        return 'insurance-icon';
    }
  }
</script>

<style scoped lang="postcss">
  .shapagat-option {
    @apply relative flex justify-center;

    &:before {
      @apply duration-300;
      content: '';
      width: 24px;
      height: 24px;
      position: absolute;
      top: 0;
      background-image: url('@/assets/images/sprout-icon.svg');
      background-repeat: no-repeat;
      background-position: right center;
      background-size: cover;
    }

    &[show-sprout-icon='true'] {
      &:before {
        transform: translateY(-65%);
      }
    }
  }
</style>
